// Countertops/Cabinets manual estimate — pure helpers for the OrderConfirmation-shaped material &
// labor item model. The C/C quantity page renders the same Material/Labor tables as
// OrderConfirmation.jsx and pulls rows in via the shared PricingContract "Add Material/Labor"
// modal, so line items carry that screen's fields (quantity, unit/unitName, privateName, style,
// color, unitPrice, price, linePrice, gpm, lineCost, laborCategory, labor/laborName, ...).
//
// GPM convention mirrors OrderConfirmation exactly: per-line job cost is `lineCost` when present,
// otherwise derived from the entered gpm as linePrice x (100 - gpm) / 100.

const toNumber = (v) => Number(String(v ?? "").trim());
const num = (v) => {
  const n = toNumber(v);
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n) => Math.round(n * 100) / 100;

// Effective unit price for a line (unitPrice, falling back to price — OrderConfirmation convention).
export function effectiveUnitPrice(item = {}) {
  const up = toNumber(item.unitPrice);
  if (!Number.isNaN(up) && String(item.unitPrice ?? "").trim() !== "") return up;
  return num(item.price);
}

// Line extended price = qty x effective unit price.
export function lineExtended(item = {}) {
  return round2(num(item.quantity) * effectiveUnitPrice(item));
}

// Return the item with linePrice recomputed (2dp string, matching the grid's display).
export function recalcLinePrice(item = {}) {
  return { ...item, linePrice: lineExtended(item).toFixed(2) };
}

// Per-line job cost. Precedence:
//   1. qty x unitCost   — product/kit rows carry a per-unit cost (currentCost); cost scales with qty
//   2. lineCost         — an explicit per-line cost
//   3. linePrice x (100 - gpm) / 100  — derive from the entered GPM (OrderConfirmation fallback)
export function lineJobCost(item = {}) {
  if (item.unitCost !== undefined && item.unitCost !== null && String(item.unitCost).trim() !== "") {
    return round2(num(item.quantity) * num(item.unitCost));
  }
  if (item.lineCost !== undefined && item.lineCost !== null && String(item.lineCost).trim() !== "") {
    return round2(num(item.lineCost));
  }
  const linePrice = String(item.linePrice ?? "").trim() !== "" ? num(item.linePrice) : lineExtended(item);
  return round2((linePrice * (100 - num(item.gpm))) / 100);
}

// Effective line price for totals: stored linePrice when present, else recomputed qty x unitPrice.
function effectiveLinePrice(item = {}) {
  return String(item.linePrice ?? "").trim() !== "" ? num(item.linePrice) : lineExtended(item);
}

// Roll up a section's items into { unitPriceTotal, linePriceTotal, cost, profit, gpm }.
export function sectionTotals(items = []) {
  const list = Array.isArray(items) ? items : [];
  const unitPriceTotal = round2(list.reduce((s, i) => s + effectiveUnitPrice(i), 0));
  const linePriceTotal = round2(list.reduce((s, i) => s + effectiveLinePrice(i), 0));
  const cost = round2(list.reduce((s, i) => s + lineJobCost(i), 0));
  const profit = round2(linePriceTotal - cost);
  const gpm = linePriceTotal > 0 ? round2((profit / linePriceTotal) * 100) : 0;
  return { unitPriceTotal, linePriceTotal, cost, profit, gpm };
}

// Validate a single line's qty + price for Save.
//   quantity  — required, numeric, > 0
//   unitPrice — required (falls back to `price`), numeric, >= 0 (a $0 line is allowed)
// Returns an object with only the failing fields set to a message ({} when the line is valid).
function validateLine(item = {}) {
  const errors = {};
  const qtyRaw = String(item.quantity ?? "").trim();
  const qty = toNumber(qtyRaw);
  if (qtyRaw === "") errors.quantity = "Qty is required";
  else if (!Number.isFinite(qty)) errors.quantity = "Qty must be a number";
  else if (qty <= 0) errors.quantity = "Qty must be greater than 0";

  const hasUnitPrice = String(item.unitPrice ?? "").trim() !== "";
  const hasPrice = String(item.price ?? "").trim() !== "";
  if (!hasUnitPrice && !hasPrice) {
    errors.unitPrice = "Price is required";
  } else {
    const priceRaw = hasUnitPrice ? String(item.unitPrice).trim() : String(item.price).trim();
    const price = toNumber(priceRaw);
    if (!Number.isFinite(price)) errors.unitPrice = "Price must be a number";
    else if (price < 0) errors.unitPrice = "Price cannot be negative";
  }
  return errors;
}

// Validate all material + labor lines. Returns
// { isValid, material: { [index]: {quantity?, unitPrice?} }, labor: {...} } — only invalid lines appear.
export function validateEstimateItems(materialList = [], laborList = []) {
  const collect = (list) => {
    const out = {};
    (Array.isArray(list) ? list : []).forEach((item, index) => {
      const errs = validateLine(item);
      if (Object.keys(errs).length) out[index] = errs;
    });
    return out;
  };
  const material = collect(materialList);
  const labor = collect(laborList);
  const isValid = Object.keys(material).length === 0 && Object.keys(labor).length === 0;
  return { isValid, material, labor };
}

// --- Add Scope de-duplication -------------------------------------------------------------------
// The "Add Scope" modal can be run repeatedly, and the same structure/product may be picked again
// in a later run (e.g. Cabinet Boxes chosen in two sessions). Identify a row by its structure +
// product/labor identity (plus name/style/color as a guard when ids are missing) so re-picking it
// updates the existing line instead of duplicating it — the LATEST selection wins.
const str = (v) => String(v ?? "").trim().toLowerCase();

export function materialIdentityKey(item = {}) {
  return [
    "m",
    num(item.productStructureId),
    num(item.productId),
    num(item.materialId),
    str(item.privateName),
    str(item.style),
    str(item.color),
  ].join("|");
}

export function laborIdentityKey(item = {}) {
  return [
    "l",
    num(item.productStructureId),
    num(item.laborId),
    num(item.laborItemId),
    str(item.labor ?? item.laborName),
    str(item.laborCategory),
  ].join("|");
}

// Merge `incoming` rows into `existing`, keyed by identity: a row whose key already appears (in the
// existing list or earlier in the same batch) REPLACES that row in place (keeping its position);
// genuinely new rows are appended. The latest selection wins, so re-adding a product shows its
// newest values without duplicating the line.
export function upsertUnique(existing = [], incoming = [], keyFn) {
  const base = Array.isArray(existing) ? existing : [];
  const add = Array.isArray(incoming) ? incoming : [];
  const indexByKey = new Map();
  const result = [...base];
  result.forEach((item, i) => indexByKey.set(keyFn(item), i));
  add.forEach((item) => {
    const key = keyFn(item);
    if (indexByKey.has(key)) {
      result[indexByKey.get(key)] = item; // replace in place — latest wins
    } else {
      indexByKey.set(key, result.length);
      result.push(item);
    }
  });
  return result;
}

// Estimate total = all material + labor line prices.
export function estimateItemsTotal(materialList = [], laborList = []) {
  const mat = (Array.isArray(materialList) ? materialList : []).reduce((s, i) => s + effectiveLinePrice(i), 0);
  const lab = (Array.isArray(laborList) ? laborList : []).reduce((s, i) => s + effectiveLinePrice(i), 0);
  return round2(mat + lab);
}
